import type * as React from 'react';

import { cn } from '@/lib/ui/utils';

/** Blur ramp height — percentage masks scale with this; taller = softer edge. */
const DEFAULT_HEIGHT_PX = 128;

type BlurLayer = { blurPx: number; mask: string };

/**
 * Four stacked backdrop-blur bands — each layer masks a ~25% slice so the blur
 * ramps toward the anchored edge (Framer "Progressive Blur"). Four bands (down
 * from eight) halve the per-frame `backdrop-filter` passes — these strips are
 * fixed/sticky in the scroll path, so each layer recomposites every scroll
 * frame; on weak mobile GPUs that was the dominant landing scroll-jank cost
 * (MOR-707). The radii reuse four steps of the original ramp so the frosted
 * dissolve looks the same.
 */
const BLUR_LAYERS_BOTTOM: readonly BlurLayer[] = [
  {
    blurPx: 0.156_25,
    mask: 'linear-gradient(to bottom, transparent 0%, black 25%, black 37.5%, transparent 62.5%)',
  },
  {
    blurPx: 0.625,
    mask: 'linear-gradient(to bottom, transparent 25%, black 50%, black 62.5%, transparent 87.5%)',
  },
  {
    blurPx: 2.5,
    mask: 'linear-gradient(to bottom, transparent 50%, black 75%, black 87.5%, transparent 100%)',
  },
  {
    blurPx: 10,
    mask: 'linear-gradient(to bottom, transparent 75%, black 100%)',
  },
];

/** Vertical mirror of {@link BLUR_LAYERS_BOTTOM} — strongest blur at the top edge. */
const BLUR_LAYERS_TOP: readonly BlurLayer[] = [
  {
    blurPx: 0.156_25,
    mask: 'linear-gradient(to bottom, transparent 37.5%, black 62.5%, black 75%, transparent 100%)',
  },
  {
    blurPx: 0.625,
    mask: 'linear-gradient(to bottom, transparent 12.5%, black 37.5%, black 50%, transparent 75%)',
  },
  {
    blurPx: 2.5,
    mask: 'linear-gradient(to bottom, transparent 0%, black 12.5%, black 25%, transparent 50%)',
  },
  {
    blurPx: 10,
    mask: 'linear-gradient(to bottom, black 0%, transparent 25%)',
  },
];

export type ProgressiveBlurEdge = 'top' | 'bottom';

export interface ProgressiveBlurProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Which viewport edge the blur ramp anchors to. @default 'bottom' */
  edge?: ProgressiveBlurEdge | undefined;
  /** Blur ramp height in px. @default 128 */
  heightPx?: number | undefined;
  /** When true, adds a theme fade over the blur stack. */
  tint?: boolean | undefined;
}

const LAYERS_BY_EDGE: Record<ProgressiveBlurEdge, readonly BlurLayer[]> = {
  bottom: BLUR_LAYERS_BOTTOM,
  top: BLUR_LAYERS_TOP,
};

const TINT_CLASS_BY_EDGE: Record<ProgressiveBlurEdge, string> = {
  bottom:
    'bg-gradient-to-t from-background-secondary from-0% via-background-secondary/50 via-35% to-transparent to-100%',
  top: 'bg-gradient-to-b from-background-secondary from-0% via-background-secondary/50 via-35% to-transparent to-100%',
};

const FALLBACK_CLASS_BY_EDGE: Record<ProgressiveBlurEdge, string> = {
  bottom:
    'bg-gradient-to-t from-background-secondary from-0% via-background-secondary/60 via-40% to-transparent to-100%',
  top: 'bg-gradient-to-b from-background-secondary from-0% via-background-secondary/60 via-40% to-transparent to-100%',
};

/**
 * ProgressiveBlur — stacked masked `backdrop-filter` layers for a smooth
 * frosted dissolve at the top or bottom edge.
 *
 * On **coarse-pointer (touch) devices** the `backdrop-filter` layers are not
 * rendered — these strips are fixed/sticky in the scroll path, and stacked
 * backdrop-filters recompositing every scroll frame are the dominant landing
 * scroll-jank cost on weak mobile GPUs (MOR-707). Touch falls back to the same
 * tint/gradient as engines without `backdrop-filter` support. Fine-pointer
 * (desktop) keeps the full frosted effect.
 */
export function ProgressiveBlur({
  edge = 'bottom',
  heightPx = DEFAULT_HEIGHT_PX,
  tint = false,
  className,
  style,
  ...props
}: ProgressiveBlurProps): React.ReactNode {
  const layers = LAYERS_BY_EDGE[edge];

  return (
    <div
      aria-hidden="true"
      data-slot="progressive-blur"
      data-progressive-blur-edge={edge}
      className={cn('pointer-events-none w-full overflow-hidden', className)}
      style={{ height: heightPx, ...style }}
      {...props}
    >
      {layers.map((layer, index) => (
        <div
          key={`${edge}-${layer.blurPx}`}
          data-progressive-blur-layer={index}
          className="absolute inset-0 hidden motion-safe:[@media(pointer:fine)]:supports-[backdrop-filter]:block"
          style={{
            zIndex: index + 1,
            backdropFilter: `blur(${String(layer.blurPx)}px)`,
            WebkitBackdropFilter: `blur(${String(layer.blurPx)}px)`,
            maskImage: layer.mask,
            WebkitMaskImage: layer.mask,
          }}
        />
      ))}
      {tint ? (
        <div
          className={cn(
            'absolute inset-0 z-10 hidden motion-safe:[@media(pointer:fine)]:supports-[backdrop-filter]:block',
            TINT_CLASS_BY_EDGE[edge],
          )}
          data-testid="progressive-blur-tint"
        />
      ) : null}
      <div
        className={cn(
          'absolute inset-0 z-10 motion-safe:[@media(pointer:fine)]:supports-[backdrop-filter]:hidden',
          FALLBACK_CLASS_BY_EDGE[edge],
        )}
        data-testid="progressive-blur-fallback"
      />
    </div>
  );
}
